import { Injectable, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import type { DecodedUrlGetResultAjax } from '@freelancer/datastore/collections';
import { FreelancerHttp, type ResponseData } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { type Observable, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private auth = inject(Auth);
  private freelancerHttp = inject(FreelancerHttp);
  private location = inject(Location);

  isUrlPlainInternal(url: string): boolean {
    if (url.startsWith('/')) {
      return true;
    }
    try {
      const stripWww = (hostname: string): string =>
        hostname.replace(/^www\./, '');
      const urlHostname = stripWww(new URL(url).hostname);
      const originHostname = stripWww(new URL(this.location.origin).hostname);
      return urlHostname === originHostname;
    } catch {
      return false;
    }
  }

  /**
   * Returns the internal path (pathname + search + hash) if the URL is
   * same-site, or undefined if it is external.
   */
  getInternalUrl(url: string): string | undefined {
    if (!this.isUrlPlainInternal(url)) {
      return undefined;
    }
    if (url.startsWith('/')) {
      return url;
    }
    const { pathname, search, hash } = new URL(url);
    return `${pathname}${search}${hash}`;
  }

  decode(
    encodedUrl: string,
  ): Observable<ResponseData<DecodedUrlGetResultAjax, Error> | undefined> {
    return this.auth
      .getUserId()
      .pipe(
        switchMap(userId =>
          this.freelancerHttp.get<DecodedUrlGetResultAjax>(
            `decoded-urls/getDecodedUrl.php?inputUrl=${encodedUrl}&userId=${userId}`,
            { isGaf: true },
          ),
        ),
      );
  }
}
